import { useEffect, useState } from 'react';
import { ServerApi } from '../services/sync/ServerApi';

interface Props {
  busy: boolean;
  error: string | null;
  baseUrl: string;
  onLogin: (email: string, password: string) => Promise<void>;
}

function currentResetToken(): string | null {
  try { return new URLSearchParams(window.location.search).get('reset'); } catch { return null; }
}

export function LoginScreen({ busy, error, baseUrl, onLogin }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetToken] = useState(currentResetToken);
  const [resetEmail, setResetEmail] = useState('');
  const [resetExpiresAt, setResetExpiresAt] = useState('');
  const [resetStatus, setResetStatus] = useState<'checking'|'ready'|'done'|'invalid'>(resetToken ? 'checking' : 'invalid');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    if (!resetToken) return;
    let cancelled = false;
    new ServerApi(baseUrl).validatePasswordReset(resetToken)
      .then((result) => { if (!cancelled) { setResetEmail(result.email); setResetExpiresAt(result.expiresAt); setResetStatus('ready'); } })
      .catch((cause) => { if (!cancelled) { setResetError(cause instanceof Error ? cause.message : 'Link inválido.'); setResetStatus('invalid'); } });
    return () => { cancelled = true; };
  }, [baseUrl, resetToken]);

  async function submitReset(event: { preventDefault(): void }) {
    event.preventDefault();
    if (!resetToken || resetBusy || resetStatus !== 'ready') return;
    if (resetPassword.length < 12) { setResetError('A nova senha deve ter ao menos 12 caracteres.'); return; }
    if (resetPassword !== resetConfirm) { setResetError('As senhas não conferem.'); return; }
    setResetBusy(true); setResetError(null);
    try {
      await new ServerApi(baseUrl).resetPassword(resetToken, resetPassword);
      setResetStatus('done'); setPassword(''); setEmail(resetEmail);
      try { window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`); } catch { /* não crítico */ }
    } catch (cause) { setResetError(cause instanceof Error ? cause.message : 'Não foi possível redefinir a senha.'); }
    finally { setResetBusy(false); }
  }

  const resetMode = Boolean(resetToken) && resetStatus !== 'done';
  const allowAutoFocus = typeof window !== 'undefined' && window.matchMedia('(min-width: 761px) and (pointer: fine)').matches;

  return (
    <main className="login-shell">
      <section className="login-brand-panel" aria-label="OWDEX Draw">
        <div className="login-brand-lockup">
          <img className="login-logo-image" src="/brand/owdex-draw-mark.png" alt="" aria-hidden="true" />
          <div><strong>OWDEX Draw</strong><span>Diagramas, arquitetura e processos em um só lugar.</span></div>
        </div>
        <div className="login-hero-copy">
          <span className="login-kicker">PLATAFORMA VISUAL OWDEX</span>
          <h1>Do processo à arquitetura, tudo ganha forma.</h1>
          <p>Crie diagramas profissionais, organize conhecimento e trabalhe com sua equipe em uma experiência rápida, segura e integrada à OWDEX.</p>
          <div className="login-feature-grid">
            <div><b>⚡</b><span><strong>Rápido</strong><small>Renderização e edição no seu computador.</small></span></div>
            <div><b>↗</b><span><strong>Compartilhável</strong><small>Links públicos controlados e exportação em PDF.</small></span></div>
            <div><b>◫</b><span><strong>Organizado</strong><small>Projetos, modelos e empresas administrados pelo Master.</small></span></div>
          </div>
        </div>
        <small className="login-footnote">OWDEX • draw.owdex.com.br</small>
      </section>
      <section className="login-form-panel">
        {resetMode ? <form className="login-card reset-password-card" onSubmit={(event)=>void submitReset(event)}>
          <div className="login-card-head"><span>Redefinição segura</span><h2>Crie uma nova senha</h2><p>{resetStatus === 'checking' ? 'Validando o link…' : resetEmail ? `Conta: ${resetEmail}` : 'Use o link enviado pelo administrador.'}</p></div>
          {resetStatus === 'ready' && <>
            <label>Nova senha<div className="login-password"><input autoFocus={allowAutoFocus} type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={resetPassword} onChange={(event)=>setResetPassword(event.target.value)} minLength={12} placeholder="12 ou mais caracteres" required /><button type="button" onClick={()=>setShowPassword((value)=>!value)}>{showPassword?'Ocultar':'Mostrar'}</button></div></label>
            <label>Confirmar senha<input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={resetConfirm} onChange={(event)=>setResetConfirm(event.target.value)} minLength={12} placeholder="Repita a nova senha" required /></label>
            {resetExpiresAt && <div className="login-reset-expiry">Link válido até {new Date(resetExpiresAt).toLocaleString('pt-BR')}.</div>}
            {resetError && <div className="login-error" role="alert">{resetError}</div>}
            <button type="submit" className="login-submit" disabled={resetBusy}>{resetBusy ? 'Alterando…' : 'Alterar senha'}</button>
          </>}
          {resetStatus === 'checking' && <div className="login-reset-state">Validando link de redefinição…</div>}
          {resetStatus === 'invalid' && <><div className="login-error" role="alert">{resetError ?? 'Link inválido, expirado ou já utilizado.'}</div><button type="button" className="login-submit" onClick={()=>{ window.history.replaceState({},'',window.location.pathname); window.location.reload(); }}>Voltar ao login</button></>}
        </form> : <form className="login-card" onSubmit={(event) => { event.preventDefault(); void onLogin(email, password); }}>
          <div className="login-card-head"><span>Acesso seguro</span><h2>{resetStatus === 'done' ? 'Senha alterada com sucesso' : 'Entre no OWDEX Draw'}</h2><p>{resetStatus === 'done' ? 'Use a nova senha para entrar.' : 'Use o e-mail e a senha cadastrados para sua empresa.'}</p></div>
          <label>E-mail<input autoFocus={allowAutoFocus} type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="voce@empresa.com" required /></label>
          <label>Senha<div className="login-password"><input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} placeholder="Sua senha" required /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>{showPassword ? 'Ocultar' : 'Mostrar'}</button></div></label>
          {error && <div className="login-error" role="alert">{error}</div>}
          <button type="submit" className="login-submit" disabled={busy}>{busy ? 'Entrando…' : 'Entrar'}</button>
          <div className="login-security-note"><span>●</span> Conexão protegida por HTTPS. Sua senha não fica armazenada no navegador.</div>
        </form>}
      </section>
    </main>
  );
}
